import {
  FC,
  useContext,
  useEffect,
  useState,
  ChangeEvent,
  useRef,
} from "react";
import { useModalAction } from "../../../components/modal-views/context";
import { useMutation } from "react-query";
import client from "../../../api";
import {
  capitalizeEachFirstLetter,
  capitalizeFirstLetter,
  convertChannelNameFee,
  convertServiceName,
  extractBillingCategories,
  getErrorMessage,
  getStatusStyle,
  logger,
  makeNumberWithFloatingDigits,
} from "../../../lib/utils";
import { convertPaymentSystem } from "../../../lib/utils";
import GlobalContext from "src/context/global-context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { Form, Input, InputNumber, Table } from "antd";
import {
  NetworkDisconnectMsg,
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import PermissionGate from "src/components/shared/permission-gate";
import {
  DailyLimit,
  Fee,
  Limitation,
  PaginationInput,
  ParamsSort,
  ServiceParamsSort,
  User,
} from "src/api/types";
import SearchUserIDBox from "src/components/shared/search-box";
import {
  DailyLimitDataType_1,
  FeeDataType,
  LimitationDataType,
  UserDataType,
} from "src/lib/constants/table-type";
import { ColumnsType } from "antd/es/table";
import { LockIcon } from "src/components/icons/lock-icon";
import { UnlockIcon } from "src/components/icons/unlock-icon";
import Button from "src/components/ui/button";
import { BackCircleIcon } from "src/components/icons/back-circle-icon";
import UsersContainerPage from "src/components/users";
import { EditIcon } from "src/components/icons/edit-icon";
import { CloseIcon } from "src/components/icons/close-icon";
import { CheckIcon } from "src/components/icons/check-icon";
import CurrencyDrop from "src/components/ui/dropdown-list";
import fi from "date-fns/esm/locale/fi/index.js";

interface IGroupedCategoryList {
  label: string;
  code: Number;
}

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: "number" | "text";
  record: FeeDataType;
  index: number;
  children: React.ReactNode;
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode = inputType === "number" ? <InputNumber /> : <Input />;

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

interface DailyLimitationsPageProps {
  permissionModule?: string | string[];
}

const DailyLimitationsPage: FC<DailyLimitationsPageProps> = ({
  permissionModule = permissionConstants.LIMITATIONS_MONEY_MOVERS_INDIVIDUAL,
}) => {
  const { canView, canList, canEdit } = usePermissions(permissionModule);
  const canViewLimitations = canView || canList;

  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isSmall, setIsSmall] = useState<boolean>(false);
  const [editingKey, setEditingKey] = useState("");
  const isEditing = (record: DailyLimitDataType_1) => record.key === editingKey;
  const { openModal, closeModal } = useModalAction();
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState<any>([]);
  const [categoriesList, setCategoriesList] = useState<IGroupedCategoryList[]>(
    []
  );
  const [services, setServices] = useState<any>(["All"]);
  const [selectedStatus, setSelectedStatus] = useState<any>(["All"]);
  const [catData, setCatData] = useState<any>([]);
  // const [originalDataSource, setOriginalDataSource] = useState<
  //     DailyLimitDataType_1[]
  // >([]);
  const [dataSource, setDataSource] = useState<DailyLimitDataType_1[]>([]);
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const [updatedData, setUpdatedData] = useState<DailyLimitDataType_1 | null>(
    null
  );

  const [searchParams, setSearchParams] = useState<ServiceParamsSort>({
    channel_category: { label: "", code: null },
    channel_name: "",
    current_page: 1,
    per_page: LIST_PAGE_SIZE,
    service: "",
  });
  const [dataCurrency, setDataCurrency] = useState<string[]>([]);
  const [dataChanels, setDataChanels] = useState<string[]>([]);
  const [error, setError] = useState<any>();

  const onPageChange = (page: number, pageSize: number) => {
    setSearchParams({
      ...searchParams,
      current_page: page,
    });
  };

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    setIsMobile(window.innerWidth < 640 ? true : false);
    setIsSmall(window.innerWidth < 480 ? true : false);
  }

  const { mutate: getList, isLoading } = useMutation(
    client.billingsDailyLimits.all,
    {
      onSuccess: (data: any) => {
        setTotal(data.total);
        const sortData = data.data.sort(
          (a: any, b: any) => a.asset_code - b.asset_code
        );
        setDataSource(
          sortData.map((item: any) => ({
            key: item.id,
            ...item,
          }))
        );
        // setOriginalDataSource(
        //   sortData.map((item) => ({
        //     key: item.id,
        //     ...item,
        //   }))
        // );
        return;
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    }
  );

  const editOnMobile = (key: number) => {
    if (!canEdit) return;
    const selData = dataSource.filter((item) => item.key === key)[0];
    openModal("UPDATE_DAILY_LIMITATIONS_VIEW", {
      ...selData,
      callback: onCallBack,
    });
  };

  const onCallBack = (data: DailyLimit) => {
    logger.log(data);
    setEditingKey(String(data.id));
    setUpdatedData({ key: data.id, ...data });

    setTimeout(() => {
      updateLimitations(data);
    }, 100);
  };

  const { mutate: updateLimitations, isLoading: isLoadingUpdate } = useMutation(
    client.billingsDailyLimits.update,
    {
      onSuccess: (data) => {
        logger.log(data);
        if (updatedData) {
          const newData = [...dataSource];
          const index = newData.findIndex((item) => editingKey == item.key);
          if (index > -1) {
            const item = newData[index];
            newData.splice(index, 1, {
              ...item,
              ...updatedData!,
            });
            setDataSource(newData);
            // setOriginalDataSource(newData);
            setEditingKey("");
          } else {
            newData.push(updatedData!);
            setDataSource(newData);
            // setOriginalDataSource(newData);
            setEditingKey("");
          }
        } else {
          setEditingKey("");
        }
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    }
  );

  const { mutate: getFeesList } = useMutation(client.fee.listFee, {
    onSuccess: (data) => {
      setDataCurrency(["All", ...data]);
    },
    onError: (error: any) => {
      if (error.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else {
        if (error.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }
      }
      setIsAlertOpened(true);
    },
  });

  //   const { mutate: getChanelsList } = useMutation(client.fee.listChanels, {
  //     onSuccess: (data) => {
  //       logger.log(data, "ldddlddlddd");
  //       setDataChanels(["All", ...data]);
  //     },
  //     onError: (error: any) => {
  //       if (error.code === "ERR_NETWORK") {
  //         setAlertText(NetworkDisconnectMsg);
  //       } else {
  //         if (error.response) {
  //           setAlertText(getErrorMessage(error.response.data));
  //         } else {
  //           setAlertText(APISomethingWrongMsg);
  //         }
  //       }
  //       setIsAlertOpened(true);
  //     },
  //   });

  const { mutate: getCategoriesList } = useMutation(client.categories.all, {
    onSuccess: (data) => {
      setCatData(data);
      const formattedCategoriesList: IGroupedCategoryList[] = [
        {
          label: "All",
          code: -1,
        },
        ...data.map((category: any) => ({
          label: category.cat_name.at(-1),
          code: category.id,
        })),
      ];
      setCategoriesList(formattedCategoriesList);
      setCategories(["All", ...data.map((item: any) => item.cat_name.at(-1))]);
    },
    onError: (error: any) => {
      if (error.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else {
        if (error.response) {
          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }
      }
      setIsAlertOpened(true);
    },
  });

  const handleCategory = (value: string): void => {
    if (value === "All") {
      setSearchParams({
        ...searchParams,
        channel_category: { label: "All", code: null },
      });
    } else {
      let findObj: any = categoriesList.find(
        (category: any) => category.label === value
      );
      let currCode = findObj.code;
      setSearchParams({
        ...searchParams,
        channel_category: { label: value, code: currCode },
      });
    }
    if (error?.channel_category) {
      setError((prev: any) => ({
        ...prev,
        channel_category: undefined,
      }));
    }
  };

  const handleWalletSelect = (value: string, type?: string): void => {
   if (type === "service") {
      if (value === "All") {
        setSearchParams({ ...searchParams, service: "" });
      } else {
        setSearchParams({ ...searchParams, service: value });
      }
      if (error?.service) {
        setError((prev: any) => ({
          ...prev,
          service: undefined,
        }));
      }
    }
  };

  useEffect(() => {
    setSearchParams({ ...searchParams, service: "" });
  }, [searchParams.channel_category]);

  useEffect(() => {
    // Reset current_page to 1 if any of the search parameters change (except current_page)
    if (isMounted) {
      setSearchParams((prev) => ({
        ...prev,
        current_page: 1,
      }));
    }
  }, [
    searchParams.channel_category,
    searchParams.channel_name,
    searchParams.service,
  ]);

  useEffect(() => {
    if (isMounted && canViewLimitations) {
      getList({
        userType: "individual",
        type: "daily",
        channel_category: searchParams.channel_category.code,
        channel_name: convertServiceName(searchParams.service || ""),
        current_page: searchParams.current_page,
        per_page: searchParams.per_page,
      });
    }
  }, [
    isMounted,
    canViewLimitations,
    searchParams.channel_category,
    searchParams.service,
    searchParams.current_page,
  ]);

  // useEffect(() => {
  //   let filteredData = originalDataSource;
  //   if (searchParams.asset_code && searchParams.asset_code !== "All") {
  //     filteredData = filteredData.filter(
  //       (item) => item.asset_code === searchParams.asset_code
  //     );
  //   }
  //   if (searchParams.service && searchParams.service !== "All") {
  //     filteredData = filteredData.filter(
  //       (item) => item.channel_name === extractBillingCategories(searchParams?.service || "")
  //     );
  //   }
  //   setDataSource(filteredData);

  //   logger.log(filteredData, "FITER +++");
  // }, [searchParams, ]);

  useEffect(() => {
    if(!isMounted){
      return;
    }
    getCategoriesList();

  },[isMounted])

  const edit = (record: Partial<DailyLimitDataType_1> & { key: React.Key }) => {
    if (!canEdit) return;
    form.setFieldsValue({ ...record });
    setEditingKey(record.key as string);
  };

  const cancel = () => {
    setEditingKey("");
  };

  const save = async (key: React.Key) => {
    if (!canEdit) return;
    try {
      const row = (await form.validateFields()) as DailyLimitDataType_1;
      logger.log(row);
      setUpdatedData(row);

      setTimeout(() => {
        updateLimitations({ ...row, id: key as number });
      }, 300);
    } catch (errInfo) {
      logger.log("Validate Failed:", errInfo);
    }
  };

  useEffect(() => {
    if (searchParams?.channel_category.label !== "All") {
      const filterData = catData.filter(
        (item: any) => item.cat_name[2] === searchParams?.channel_category.label
      );
      if (filterData.length > 0 && filterData[0].services?.length > 0) {
        setServices([
          "All",
          ...filterData[0].services?.map((item: any) => item.service_name[2]),
        ]);
      } else {
        setServices(["All"]);
      }
    }else{
      setServices(["Select Category"]);
    }
  }, [categories, searchParams]);

  const columns = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          Currency name
        </p>
      ),
      dataIndex: "asset_code",
      key: "asset_code",
      className: "w-auto",
      editable: false,
      render: (value: string) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-medium">
            {value?.toUpperCase()}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          Channel Name
        </p>
      ),
      dataIndex: "channel_name",
      key: "channel_name",
      className: "w-auto",
      editable: false,
      render: (value: string) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[14px] font-medium">
            {convertChannelNameFee(value)}
          </p>
        </div>
      ),
    },
    // {
    //   title: () => (
    //     <p className="font-manrope text-secondary text-[12px] font-medium">
    //       Deposit max
    //     </p>
    //   ),
    //   dataIndex: "deposit_max",
    //   key: "deposit_max",
    //   className: "w-auto",
    //   editable: true,
    //   render: (value: number) => (
    //     <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
    //       {makeNumberWithFloatingDigits(value, 2)}
    //     </p>
    //   ),
    // },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          Withdraw max
        </p>
      ),
      dataIndex: "withdraw_max",
      key: "withdraw_max",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {makeNumberWithFloatingDigits(value, 2)}
        </p>
      ),
    },
    // {
    //   title: () => (
    //     <p className="font-manrope text-secondary text-[12px] font-medium">
    //       VIP Deposit max
    //     </p>
    //   ),
    //   dataIndex: "vip_deposit_max",
    //   key: "vip_deposit_max",
    //   className: "w-auto",
    //   editable: true,
    //   render: (value: number) => (
    //     <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
    //       {makeNumberWithFloatingDigits(value, 2)}
    //     </p>
    //   ),
    // },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[12px] font-medium">
          VIP Withdraw max
        </p>
      ),
      dataIndex: "vip_withdraw_max",
      key: "vip_withdraw_max",
      className: "w-auto",
      editable: true,
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {makeNumberWithFloatingDigits(value, 2)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Action
        </p>
      ),
      dataIndex: "action",
      className: "w-auto",
      render: (_: any, record: DailyLimitDataType_1) => {
        if (!canEdit) {
          return "—";
        }
        const editable = isEditing(record);
        return editable ? (
          <div className="flex flex-row gap-2">
            <button
              className="w-[30px] h-[30px] flex items-center justify-start"
              onClick={cancel}
            >
              <CloseIcon className="w-[20px] h-[20px] text-secondary" />
            </button>
            <button
              className="w-[30px] h-[30px] flex items-center justify-start"
              onClick={() => save(record.key)}
            >
              <CheckIcon className="w-[20px] h-[20px] text-primary" />
            </button>
          </div>
        ) : (
          <div className="flex flex-row gap-2">
            <button
              className="w-[30px] h-[30px] flex items-center justify-start"
              onClick={() => edit(record)}
            >
              <EditIcon className="w-[20px] h-[20px] text-primary" />
            </button>
          </div>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: DailyLimitDataType_1) => ({
        record,
        inputType: "number",
        dataIndex: col.dataIndex,
        title: "",
        editing: isEditing(record),
      }),
    };
  });

  const mobileColumns: ColumnsType<DailyLimitDataType_1> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Info
        </p>
      ),
      dataIndex: "action",
      className: "w-auto",
      render: (_, record: { key: React.Key }) => {
        const selData = dataSource.filter((item) => item.key === record.key)[0];

        return (
          <div className="w-full flex flex-row gap-4 items-start justify-between">
            <div className="w-full flex flex-col items-start gap-2">
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  Currency
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {selData?.asset_code?.toUpperCase()}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  Channel Name
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {convertChannelNameFee(selData?.channel_name || "")}
                </p>
              </div>
              {/* <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  Deposit max
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {makeNumberWithFloatingDigits(String(selData.deposit_max), 2)}
                </p>
              </div> */}
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  Withd.max
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {makeNumberWithFloatingDigits(selData.withdraw_max, 2)}
                </p>
              </div>
            </div>
            <div className="w-full flex flex-col items-start gap-2">
              {/* <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  VIP Dept.max
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {makeNumberWithFloatingDigits(
                    String(selData.vip_deposit_max),
                    2
                  )}
                </p>
              </div> */}
              {/* <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  VIP With.max
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {makeNumberWithFloatingDigits(selData.vip_withdraw_max, 2)}
                </p>
              </div> */}
              <PermissionGate
                module={permissionModule}
                action="can_edit"
              >
                <div className="w-full flex flex-row items-center justify-between gap-2">
                  <p className="text-secondary text-[14px] font-manrope font-medium invisible">
                    Edit
                  </p>
                  <a
                    className="text-secondary underline hover:underline hover:text-primary text-[14px] font-manrope font-medium cursor-pointer"
                    onClick={() => editOnMobile(record.key as number)}
                  >
                    Edit
                  </a>
                </div>
              </PermissionGate>
            </div>
          </div>
        );
      },
    },
  ];

  const smallColumns: ColumnsType<DailyLimitDataType_1> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Info
        </p>
      ),
      dataIndex: "action",
      className: "w-auto",
      render: (_, record: { key: React.Key }) => {
        const selData = dataSource.filter((item) => item.key === record.key)[0];

        return (
          <div className="w-full flex flex-col gap-2">
            <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium">
                Currency
              </p>
              <p className="text-primary text-[14px] font-manrope font-medium">
                {selData?.asset_code?.toUpperCase()}
              </p>
            </div>
            <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium">
                Channel Name
              </p>
              <p className="text-primary text-[14px] font-manrope font-medium">
                {selData?.channel_name}
              </p>
            </div>
            {/* <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium">
                Dept.max
              </p>
              <p className="text-primary text-[14px] font-manrope font-medium">
                {makeNumberWithFloatingDigits(String(selData.deposit_max), 2)}
              </p>
            </div> */}
            <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium">
                Withd.max
              </p>
              <p className="text-primary text-[14px] font-manrope font-medium">
                {makeNumberWithFloatingDigits(selData.withdraw_max, 2)}
              </p>
            </div>
            {/* <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium">
                VIP Dept.max
              </p>
              <p className="text-primary text-[14px] font-manrope font-medium">
                {makeNumberWithFloatingDigits(
                  String(selData.vip_deposit_max),
                  2
                )}
              </p>
            </div> */}
            <div className="w-full flex flex-row items-center justify-between gap-2">
              <p className="text-secondary text-[14px] font-manrope font-medium">
                VIP With.max
              </p>
              <p className="text-primary text-[14px] font-manrope font-medium">
                {makeNumberWithFloatingDigits(selData.vip_withdraw_max, 2)}
              </p>
            </div>
            <PermissionGate
              module={permissionModule}
              action="can_edit"
            >
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium invisible">
                  Edit
                </p>
                <a
                  className="text-secondary underline hover:underline hover:text-primary text-[14px] font-manrope font-medium cursor-pointer"
                  onClick={() => editOnMobile(record.key as number)}
                >
                  Edit
                </a>
              </div>
            </PermissionGate>
          </div>
        );
      },
    },
  ];

  if (!canViewLimitations) {
    return (
      <div className="bg-white rounded-r-[12px] rounded-bl-[12px] w-full p-6 text-center text-secondary">
        You do not have permission to view limitations.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-r-[12px] rounded-bl-[12px] w-full relative">
      <div className="p-4 relative">
        <div className="grid gap-3 grid-cols-12">
          <div className="lg:col-span-3 md:col-span-4 sm:col-span-6 col-span-12">
            <div className="input table-filter__item" style={{ width: "100%" }}>
              <p className="input__name input__name--type2">Category</p>
              <CurrencyDrop
                value={
                  searchParams.channel_category.label === "" ||
                  !searchParams.channel_category
                    ? "All"
                    : searchParams.channel_category.label
                }
                options={categories ? [...categories] : []}
                onChange={handleCategory}
                type="category"
              />
              <p className="text-red-500">
                {error &&
                  error.channel_category &&
                  typeof error.channel_category === "string" &&
                  error.channel_category}
              </p>
            </div>
          </div>
          <div className="lg:col-span-3 md:col-span-4 sm:col-span-6 col-span-12">
            <div className="input table-filter__item" style={{ width: "100%" }}>
              <p className="input__name input__name--type2">Service</p>
              <CurrencyDrop
                value={
                  searchParams.service === "" || !searchParams.service
                    ? "All"
                    : searchParams.service
                }
                options={services ? [...services] : []}
                onChange={handleWalletSelect}
                type="service"
              />
              <p className="text-red-500">
                {error &&
                  error.service &&
                  typeof error.service === "string" &&
                  error.service}
              </p>
            </div>
          </div>
        </div>
        {isMobile ? (
          <Table
            loading={isLoading || isLoadingUpdate}
            showSorterTooltip={false}
            columns={isSmall ? smallColumns : mobileColumns}
            dataSource={dataSource}
            // pagination={false}
            pagination={{
              total,
              current: searchParams.current_page,
              onChange: onPageChange,
              position: ["bottomCenter"],
              defaultPageSize: LIST_PAGE_SIZE,
              showSizeChanger: false,
            }}
          />
        ) : (
          <Form form={form} component={false}>
            <Table
              components={{
                body: {
                  cell: EditableCell,
                },
              }}
              loading={isLoading || isLoadingUpdate}
              showSorterTooltip={false}
              columns={mergedColumns}
              dataSource={dataSource}
              // pagination={false}
              pagination={{
                total,
                current: searchParams.current_page,
                onChange: onPageChange,
                position: ["bottomCenter"],
                defaultPageSize: LIST_PAGE_SIZE,
                showSizeChanger: false,
              }}
              rowClassName="editable-row"
            />
          </Form>
        )}
      </div>
    </div>
  );
};

export default DailyLimitationsPage;
